import type { AssignmentMap, SettingsPayload, SlideSettingMap } from "../SettingsPayload";

import { parseTargetRef } from "./targetRef";

/**
 * v6 ペイロードの読み取りに必要な最小形（永続 JSON 形状をローカル再宣言する）。
 * 旧スライス `entities/prompt` を参照しないための措置で、移行の入口だけが知っていればよい。
 */
type LegacyPromptTarget = { ref: string; role: "input" | "output" };

type LegacyPrompt = {
    label?: string;
    prompt: string;
    targets: LegacyPromptTarget[];
    skip?: boolean;
    updatedAt: string;
    updatedBy: string;
};

/** v6 ペイロードのうち移行で読む部分。`conditions` / `edges` / `positions` は読まない。 */
export type LegacyV6Payload = {
    version?: number;
    prompts?: { [promptId: string]: LegacyPrompt };
    labels?: { [ref: string]: string };
    sourceTaskId?: string;
    sourceProjectId?: string;
};

/**
 * v6 の `im.prompts` を v7 の {@link SettingsPayload} へ移行する。
 *
 * 対応:
 * - `prompts[id]` → `assignments[id]`（`label` / `prompt`→`instruction` / `skip`）
 * - `targets` は **role=output だけ**を引き継ぐ。参照 430 個に対し出力先だけで 429 に達しており、
 *   入力ターゲットは実質使われていないため。
 * - `labels` の `slide:` 参照 → `slides[slideId].name`。ただし現行実装が入れていた
 *   `スライド <id>` の機械文字列は名前として意味を持たないので落とす。
 * - `labels` の `element:` 参照 → **破棄**。枠の名前は `PowerPoint.Shape.name` が正本になる。
 * - `conditions` / `edges` / `positions` → **破棄**。フロー廃止。
 *
 * ラベルが空の担当は下書き相当なので取り込まない。本文も対象も無いものも、押しただけで
 * 作られたノードなので落とす（[[2026-08-18 設計ノート アドイン ノードの新規作成と下書き]]）。
 */
export function migrateV6(legacy: LegacyV6Payload, now: string): SettingsPayload {
    const assignments: AssignmentMap = {};
    for (const [id, p] of Object.entries(legacy.prompts ?? {})) {
        const label = p.label?.trim() ?? "";
        if (label.length === 0) continue;
        const targets = p.targets.filter((t) => t.role === "output").map((t) => t.ref);
        // 本文も対象も無いものは、押しただけで作られて放置されたノード。既定ラベルが
        // 入っているため旧実装の掃除が一度も効かず溜まっていた（実測で1件混入）。
        if (p.prompt.trim().length === 0 && targets.length === 0) continue;
        assignments[id] = {
            label,
            instruction: p.prompt,
            targets,
            ...(p.skip === true ? { skip: true } : {}),
            updatedAt: p.updatedAt,
            updatedBy: p.updatedBy,
        };
    }

    const slides: SlideSettingMap = {};
    for (const [ref, name] of Object.entries(legacy.labels ?? {})) {
        const parsed = parseTargetRef(ref);
        if (parsed.kind !== "slide") continue;
        const trimmed = name.trim();
        if (trimmed.length === 0) continue;
        if (trimmed === `スライド ${parsed.officeId}`) continue;
        slides[parsed.officeId] = { name: trimmed, updatedAt: now, updatedBy: "" };
    }

    const payload: SettingsPayload = { version: 7, slides, elements: {}, assignments };
    if (legacy.sourceTaskId) payload.sourceTaskId = legacy.sourceTaskId;
    if (legacy.sourceProjectId) payload.sourceProjectId = legacy.sourceProjectId;
    return payload;
}
